import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination — luxury-styled numbered pager with prev/next.
 * Props:
 *  - page (1-based)
 *  - total
 *  - limit
 *  - onChange(newPage)
 */
export default function Pagination({ page, total, limit, onChange }) {
    if (!total || total <= limit) return null;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    if (totalPages <= 1) return null;

    const go = (p) => {
        const np = Math.min(Math.max(1, p), totalPages);
        if (np !== page) {
            onChange(np);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // Build a compact page list: 1 ... p-1 p p+1 ... last
    const pages = [];
    const add = (n) => { if (!pages.includes(n) && n >= 1 && n <= totalPages) pages.push(n); };
    add(1);
    if (page - 1 > 2) pages.push("…");
    for (let i = page - 1; i <= page + 1; i++) add(i);
    if (page + 1 < totalPages - 1) pages.push("…");
    add(totalPages);

    const from = (page - 1) * limit + 1;
    const to = Math.min(page * limit, total);

    return (
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-rk-border pt-6">
            <div className="text-xs text-rk-muted uppercase tracking-[0.18em]">
                Showing <span className="text-rk-navy font-semibold">{from}–{to}</span> of <span className="text-rk-navy font-semibold">{total}</span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="pagination">
                <button
                    onClick={() => go(page - 1)}
                    disabled={page <= 1}
                    data-testid="pagination-prev"
                    className="w-9 h-9 flex items-center justify-center border border-rk-border rounded-md text-rk-navy hover:bg-rk-cream disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                {pages.map((p, i) =>
                    p === "…" ? (
                        <span key={`e${i}`} className="px-2 text-rk-muted text-sm">…</span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => go(p)}
                            data-testid={`pagination-page-${p}`}
                            aria-current={p === page ? "page" : undefined}
                            className={`min-w-9 h-9 px-3 text-sm border rounded-md transition ${
                                p === page
                                    ? "bg-rk-navy text-white border-rk-navy font-semibold"
                                    : "border-rk-border text-rk-navy hover:bg-rk-cream"
                            }`}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    onClick={() => go(page + 1)}
                    disabled={page >= totalPages}
                    data-testid="pagination-next"
                    className="w-9 h-9 flex items-center justify-center border border-rk-border rounded-md text-rk-navy hover:bg-rk-cream disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
